import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../utils/cn";

export interface AppShellProps extends HTMLAttributes<HTMLDivElement> {
  /** Optional top bar (header, navbar). Hidden when falsy. */
  header?: ReactNode;
  /** Optional bottom bar (status bar, footer). Hidden when falsy. */
  footer?: ReactNode;
  /** Main content. Required. */
  children: ReactNode;
}

/**
 * Outer chrome for a module's app: vertical stack of
 * [optional header, main, optional footer], full viewport height,
 * brand background.
 *
 *   <AppShell header={<NavBar />} footer={<StatusBar />}>
 *     <SidebarPanelLayout>...</SidebarPanelLayout>
 *   </AppShell>
 *
 * Pass falsy values to `header` / `footer` to omit those bars.
 */
export function AppShell({
  header,
  footer,
  children,
  className,
  ...rest
}: AppShellProps) {
  return (
    <div
      className={cn("flex h-full flex-col bg-background", className)}
      {...rest}
    >
      {header && <div className="flex-shrink-0">{header}</div>}
      <div className="flex flex-1 flex-col overflow-hidden">{children}</div>
      {footer && <div className="flex-shrink-0">{footer}</div>}
    </div>
  );
}
AppShell.displayName = "AppShell";
